import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/lib/siteSettings';

const footerLinks = {
  services: [
    { label: 'Trading', href: '/trading' },
    { label: 'Procurement', href: '/procurement' },
    { label: 'Logistics', href: '/logistics' },
    { label: 'Freight Forwarding', href: '/freight-forwarding' },
    { label: 'Clearing & Forwarding', href: '/clearing-forwarding' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'Products', href: '/products' },
    { label: 'Industries', href: '/industries' },
    { label: 'News & Insights', href: '/news' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ],
  industries: [
    { label: 'Construction & Infrastructure', href: '/industries' },
    { label: 'Healthcare', href: '/industries' },
    { label: 'Manufacturing & Industrial', href: '/industries' },
    { label: 'Government & Public Sector', href: '/industries' },
    { label: 'NGOs & Development', href: '/industries' },
  ],
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { settings } = useSiteSettings();
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <footer className="bg-primary-900 text-white">
      {/* Main Footer */}
      <div className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              {logoFailed ? (
                <span className="text-2xl font-heading font-bold uppercase tracking-tight text-white">
                  BEKAALI
                </span>
              ) : (
                <img
                  src={settings.logo_url}
                  alt="BEKAALI ENTERPRISES LIMITED"
                  onError={() => setLogoFailed(true)}
                  className="h-10 w-auto object-contain"
                />
              )}
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-md">
              {settings.footer_description}
            </p>

            {/* Contact Details */}
            <div className="space-y-3 mb-6">
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 text-white/60 hover:text-accent-400 transition-colors duration-200 group cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 group-hover:bg-accent-500/20 transition-colors duration-200 flex-shrink-0">
                  <i className="ri-mail-line text-sm"></i>
                </div>
                <span className="text-sm">{settings.contact_email}</span>
              </a>
              <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-3 text-white/60 hover:text-accent-400 transition-colors duration-200 group cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 group-hover:bg-accent-500/20 transition-colors duration-200 flex-shrink-0">
                  <i className="ri-phone-line text-sm"></i>
                </div>
                <span className="text-sm">{settings.contact_phone}</span>
              </a>
              <div className="flex items-start gap-3 text-white/60">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-sm"></i>
                </div>
                <span className="text-sm leading-relaxed">{settings.contact_address}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {settings.linkedin_url && settings.linkedin_url !== '#' && (
                <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 transition-all duration-300 text-white/70 hover:text-white" aria-label="LinkedIn">
                  <i className="ri-linkedin-fill text-sm"></i>
                </a>
              )}
              {settings.twitter_url && settings.twitter_url !== '#' && (
                <a href={settings.twitter_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 transition-all duration-300 text-white/70 hover:text-white" aria-label="Twitter">
                  <i className="ri-twitter-x-fill text-sm"></i>
                </a>
              )}
              {settings.facebook_url && settings.facebook_url !== '#' && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 transition-all duration-300 text-white/70 hover:text-white" aria-label="Facebook">
                  <i className="ri-facebook-fill text-sm"></i>
                </a>
              )}
              {settings.youtube_url && settings.youtube_url !== '#' && (
                <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 transition-all duration-300 text-white/70 hover:text-white" aria-label="YouTube">
                  <i className="ri-youtube-fill text-sm"></i>
                </a>
              )}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50 mb-5">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/60 hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50 mb-5">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/60 hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50 mb-5">Industries</h4>
            <ul className="space-y-3">
              {footerLinks.industries.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-white/60 hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="w-full px-6 md:px-10 lg:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">
            &copy; {currentYear} BEKAALI ENTERPRISES LIMITED. All rights reserved. — Made by <span className="text-white/50">Lechitech Systems</span>
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-white/40 hover:text-white/60 transition-colors duration-200">Privacy Policy</a>
            <a href="#" className="text-xs text-white/40 hover:text-white/60 transition-colors duration-200">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}